// [목적] /settings — 설정 메인 (프로필·알림·데이터·정보·계정관리 5섹션)
// [이유] settings-be-wire — 헤더 본인 정보(E) + 알림 prefs 토글(D) 을 useMe + usePatchUserMe 로 실연결.
//         기존 하드코딩("강원여행러", "@gangwon.traveler · 카카오 연결됨") → useMe 실데이터 대체.
//         push 토글 로컬 useState → useMe.notificationPrefs 초기값 + patchMe 디바운스 저장.
//         MSC-FE-01: authUser.provider ?? 'kakao' fallback 제거 → authUser.primaryProvider 사용.
//           null 시 '소셜 계정' 중립 표시. providerLabel 헬퍼는 SettingsChrome 에서 공유.
//         hide-settings-email-row(2026-06-03): SHOW_EMAIL_ROW flag 로 이메일 행 조건부 비렌더.
//           SHOW_EMAIL_ROW=false 시 이메일 Row 를 DOM 에서 제거 — Tab/스크린리더 잔존 없음(WCAG AC-6).
//         M7 P2(발견 화면 필터+비공개 프로필): 프로필 공개 토글 로컬 state → useMe.profileVisibility
//           초기값 + patchMe({profileVisibility}) 실저장. "캐시 삭제" Row 활성화 —
//           react-query queryClient.clear() + Cache Storage 전체 삭제 + 완료 토스트.
// [사이드 이팩트] usePatchUserMe 성공 시 queryKeys.user.me() invalidate.
//                 useMe 로딩 중 헤더 행은 Skeleton 표시.
//                 캐시 삭제 시 QueryClient 전체 캐시 초기화 — 클릭 직후 화면들이 잠시 재조회(로딩) 상태로 전환됨.
// [수정 시 고려사항] notificationPrefs 는 BE 서버측 partial merge — 키 단위 전송.
//                   프로필 공개 토글은 patchUserMeInputSchema.profileVisibility('public'|'private') 로 저장 —
//                   me 로드 전에는 기본값 true(공개)로 낙관 표시 후 useEffect 로 서버값 동기화.
//                   알림 토글 디바운스 300ms — useRef 타이머로 구현.
//                   이메일 행 재노출 시 settings.flags.ts 의 SHOW_EMAIL_ROW 를 true 로 변경(단일 SoT).
// [수정: 2026-09-20 설정 페이지 카드 개편] 전체 폭 리스트(모바일 시안 그대로 늘어남) → 섹션별 카드 + 좌우 2단.
//   - lg(≥1200px): 좌측 = 프로필 요약 카드 + 계정 관리 카드, 우측 = 계정·프로필·알림·데이터·정보 카드 스택.
//   - lg 미만: 1단 — 프로필 요약 → 섹션 카드들 → 계정 관리 순(DOM 순서 = 모바일 순서, grid-template-areas 로 재배치).
//   - 기존 "계정" 섹션 첫 행(이름/핸들 → /settings/profile)은 프로필 요약 카드 자체가 대체(role=button 동일).
//   동작(토글 저장/디바운스/캐시 삭제/라우팅/SHOW_EMAIL_ROW)은 변경 없음 — 레이아웃·표현만 교체.
// [수정: 2026-09-21 "계정" 섹션 → "계정 관리" 통합] 항목이 비밀번호 변경 1개뿐이던 "계정" 섹션을 삭제하고,
//   그 안의 (이메일[flag] +) 비밀번호 변경 Row 를 "계정 관리" 섹션 맨 위로 이동 — 로그아웃/회원 탈퇴와 함께
//   계정 자체에 대한 조작을 한 곳(계정 관리)에 모음. 순서는 위험도 낮음 → 높음: 비밀번호 변경 → 로그아웃 → 회원 탈퇴.
//   [사이드 이팩트] main 컬럼(우측)은 프로필 섹션부터 시작 — Tab 순서상 프로필 요약 카드 다음은
//     바로 "프로필 공개" 토글로 이동함(과거엔 비밀번호 변경 Row 였음). SHOW_EMAIL_ROW=true 재노출 시
//     이메일 Row 도 함께 계정 관리 섹션으로 이동 — settings.flags.ts 는 변경 없음(동일 SoT 유지).

import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../hooks/useAuth.js';
import { useMe } from '../../hooks/useUserProfile.js';
import { usePatchUserMe } from '../../hooks/usePatchUserMe.js';
import { useToast } from '../../components/feedback/Toast.js';

import {
  SetHeader,
  ST_BG1,
  ST_BORDER,
  ST_BRAND,
  ST_DANGER,
  ST_SURFACE,
  ST_TEXT_HI,
  ST_TEXT_LOW,
  ST_TEXT_MID,
  providerLabel,
} from './SettingsChrome.js';
import { SHOW_EMAIL_ROW } from './settings.flags.js';

// ─── 타입 ────────────────────────────────────────────────────────────────────

interface PushPrefs {
  social: boolean;
  marketing: boolean;
  system: boolean;
}

// ─── 공통 컴포넌트 ────────────────────────────────────────────────────────────

/**
 * [목적] 설정 섹션 카드 — 제목 헤더 + Row 목록을 둥근 카드 한 장으로 묶는다
 * [이유] 기존 Section 은 회색 배경 위에 전체 폭 흰 띠(상하 보더)만 그려 데스크톱에서 화면 끝까지 늘어났다.
 *         카드(둥근 모서리 + 보더)로 바꿔 섹션 경계를 분명히 하고 좌우 2단 배치에서도 폭을 유지한다.
 * [사이드 이팩트] 마지막 Row 의 하단 보더는 Row 의 &:last-child 규칙이 제거 — 카드 자식은 Row 만 둘 것.
 *                 제목은 <h2> — 페이지 h1(SetHeader) 아래 문서 아웃라인이 생긴다.
 * [수정 시 고려사항] overflow:hidden 은 Row hover 배경이 모서리를 넘지 않게 하는 용도 —
 *                   Row 안에 팝오버/툴팁을 넣으면 잘리므로 portal 사용.
 */
function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Box
      component="section"
      sx={{
        bgcolor: ST_SURFACE,
        border: `1px solid ${ST_BORDER}`,
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: ST_TEXT_MID,
          letterSpacing: '-0.1px',
          p: '16px 20px 8px',
          m: 0,
        }}
      >
        {title}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );
}

function Row({
  icon,
  label,
  sub,
  right,
  onClick,
  danger,
}: {
  icon?: string;
  label: string;
  sub?: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
}): JSX.Element {
  return (
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        p: '12px 20px',
        borderBottom: `1px solid ${ST_BORDER}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 120ms',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': onClick ? { bgcolor: ST_BG1 } : undefined,
        '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: -2 },
      }}
    >
      {icon && (
        <Box
          aria-hidden="true"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: danger ? '#FFE4E4' : ST_BG1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 600,
            color: danger ? ST_DANGER : ST_TEXT_HI,
            letterSpacing: '-0.2px',
          }}
        >
          {label}
        </Typography>
        {sub && (
          <Typography component="div" sx={{ fontSize: 11.5, color: ST_TEXT_MID, mt: '2px' }}>
            {sub}
          </Typography>
        )}
      </Box>
      {right}
    </Box>
  );
}

/**
 * [목적] Toggle 컴포넌트 — 접근성 준수 aria-pressed 토글 버튼
 * [이유] 알림 prefs / 프로필 공개 토글 공통 사용. aria-pressed + aria-label 로 스크린리더 지원
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] disabled 상태 추가 시 tabIndex=-1 + aria-disabled 처리 필요
 */
function Toggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={() => !disabled && onChange(!value)}
      aria-label={value ? '꺼짐으로 변경' : '켜짐으로 변경'}
      aria-pressed={value}
      disabled={disabled}
      sx={{
        position: 'relative',
        width: 38,
        height: 22,
        borderRadius: 99,
        bgcolor: value ? ST_BRAND : '#D5DCE5',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
        '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: 2,
          left: value ? 18 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          bgcolor: ST_SURFACE,
          boxShadow: '0 1px 3px rgba(0,0,0,.2)',
          transition: 'left 150ms',
        }}
      />
    </ButtonBase>
  );
}

const Chev = (): JSX.Element => (
  <Box component="span" aria-hidden="true" sx={{ fontSize: 14, color: ST_TEXT_LOW }}>
    ›
  </Box>
);

// ─── SettingsPage ─────────────────────────────────────────────────────────────

/**
 * [목적] /settings 설정 메인 페이지 — useMe + usePatchUserMe 실연결
 * [이유] settings-be-wire 작업 D + E —
 *         E: 헤더 행 하드코딩 → useMe 실데이터(displayName/handle/primaryProvider)
 *         D: 알림 push 토글 로컬 → useMe.notificationPrefs 초기값 + patchMe 디바운스 저장.
 *         MSC-FE-01: authUser.provider ?? 'kakao' fallback 제거 → authUser.primaryProvider 직접 사용.
 *         null 시 '소셜 계정' 중립 표시 — 카카오 오표시 금지.
 * [사이드 이팩트] useMe 로딩 중 헤더 계정 행에 Skeleton 표시.
 *                 알림 토글 변경 300ms 디바운스 후 PATCH 전송 — 빠른 연속 토글 시 마지막 값만 전송.
 *                 providerLabel 헬퍼를 SettingsChrome.tsx 에서 import.
 * [수정 시 고려사항] pub(프로필 공개) 토글은 BE PATCH /users/me 에 해당 필드 미확인 → 로컬 유지.
 *                   primaryProvider 는 SID-BE-01 이후 MeOutput 에 포함됨 → authUser 에서 직접 참조.
 */
export function SettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const { logout, user: authUser } = useAuth();
  const { me, isLoading: meLoading } = useMe();
  const { mutate: patchMe } = usePatchUserMe();
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();

  const go = (page: string): void => navigate(`/settings/${page}`);

  // [목적] 알림 prefs 로컬 상태 — useMe 로드 후 초기화, 토글 즉시 반영
  // [이유] 서버 응답 대기 없이 토글이 즉각 반응하도록 로컬 상태로 관리
  // [사이드 이팩트] 디바운스 후 patchMe 호출 — 실패 시 서버값과 로컬값 불일치 (재조회로 복구)
  // [수정 시 고려사항] 저장 실패 후 재조회로 로컬 상태 복원이 필요하면 onError 에서 setPrefs(서버값) 추가
  const [push, setPush] = useState<PushPrefs>({ social: true, marketing: false, system: true });
  const [pushInitialized, setPushInitialized] = useState(false);

  // [목적] 프로필 공개 토글 로컬 상태 — useMe.profileVisibility 초기값으로 동기화
  // [이유] M7 P2 — 기존 로컬 전용 pub state 를 PATCH /users/me{profileVisibility} 실저장으로 교체.
  // [사이드 이팩트] toggle 클릭 시 즉시 반영(로컬) + patchMe 호출 → 성공 시 useMe invalidate 로 서버값 재동기화.
  // [수정 시 고려사항] me 로드 전 기본값은 'public'(공개) — pubInitialized 로 로드 후 1회만 서버값 반영.
  const [pub, setPub] = useState(true);
  const [pubInitialized, setPubInitialized] = useState(false);

  useEffect(() => {
    if (me && !pubInitialized) {
      setPub(me.profileVisibility !== 'private');
      setPubInitialized(true);
    }
  }, [me, pubInitialized]);

  const handlePubChange = useCallback(
    (next: boolean): void => {
      setPub(next);
      patchMe({ profileVisibility: next ? 'public' : 'private' });
    },
    [patchMe],
  );

  // [목적] 로컬 캐시 삭제 — react-query 전체 캐시 + Cache Storage(SW) 전체 초기화
  // [이유] M7 P2 — 설정 "캐시 삭제" Row 가 지금까지 비활성(문구만 존재)이던 것을 실제 동작으로 연결.
  // [사이드 이팩트] queryClient.clear() 로 모든 화면이 재조회(로딩) 상태로 전환됨. Cache Storage 삭제는
  //                PWA Service Worker 가 캐싱한 정적 자산도 함께 비워 다음 로드 시 재요청 발생시킬 수 있음.
  // [수정 시 고려사항] caches API 미지원 환경(구형 브라우저)에서는 조용히 스킵 — queryClient.clear() 는 항상 수행.
  const handleClearCache = useCallback((): void => {
    queryClient.clear();
    if (typeof caches !== 'undefined') {
      void caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .catch(() => {
          // ponytail: Cache Storage 실패는 조용히 무시 — queryClient.clear() 는 이미 수행됨
        });
    }
    showToast('캐시를 삭제했습니다.', 'success');
  }, [queryClient, showToast]);

  // [목적] useMe 로드 완료 시 알림 prefs 초기화
  // [이유] notificationPrefs 는 JSONB arbitrary — 타입 보호 후 boolean 추출
  // [사이드 이팩트] initialized 플래그 — 저장 후 invalidate 시 재덮어쓰기 방지
  // [수정 시 고려사항] notificationPrefs 키 이름이 BE 와 다르면 여기 매핑 수정
  useEffect(() => {
    if (me && !pushInitialized) {
      const prefs = me.notificationPrefs as Record<string, unknown> | undefined;
      setPush({
        social: prefs?.['social'] !== false,
        marketing: prefs?.['marketing'] === true,
        system: prefs?.['system'] !== false,
      });
      setPushInitialized(true);
    }
  }, [me, pushInitialized]);

  // [목적] 알림 토글 변경을 300ms 디바운스 후 PATCH 전송
  // [이유] 빠른 연속 토글 시 다중 API 호출 방지
  // [사이드 이팩트] 컴포넌트 언마운트 시 pending 타이머 정리 (clearTimeout)
  // [수정 시 고려사항] 타이머 ref 는 최신 push 값을 캡처하도록 closure 주의
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePushChange = useCallback(
    (next: PushPrefs): void => {
      setPush(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        patchMe({
          notificationPrefs: {
            social: next.social,
            marketing: next.marketing,
            system: next.system,
          },
        });
      }, 300);
    },
    [patchMe],
  );

  // 컴포넌트 언마운트 시 디바운스 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // [목적] 로그아웃 핸들러 — useAuth().logout 으로 BE call + 전체 reload
  // [이유] 2026-05-17 reauth-fix — navigate('/login') 만으론 BE 쿠키 미정리
  // [사이드 이팩트] window.location.href='/login' — React 트리 전체 초기화
  // [수정 시 고려사항] logout 정책 변경 시 useAuth.ts logout 메서드와 동시 갱신
  const handleLogout = (): void => {
    void logout();
  };

  // [목적] primaryProvider 레이블 — SID-BE-01로 /auth/me 에 primaryProvider 추가됨
  // [이유] MSC-FE-01: authUser.provider ?? 'kakao' fallback 제거 → me.primaryProvider 직접 사용.
  //         primaryProvider=null 이면 '소셜 계정'(중립) 표시 — 카카오 오표시 금지.
  // [사이드 이팩트] providerLabel 헬퍼는 SettingsChrome.tsx 에 추출됨 (3개 화면 공통).
  // [수정 시 고려사항] me(GET /users/me)와 authUser(GET /auth/me)는 별도 엔드포인트.
  //                   me에 primaryProvider 미포함 → authUser.primaryProvider 우선, me 로드 시 동기.
  const resolvedProvider = authUser?.primaryProvider ?? null;
  const accountSubLine = meLoading ? (
    <Skeleton width={160} height={14} />
  ) : (
    `@${me?.handle ?? ''} · ${providerLabel(resolvedProvider, '연결됨')}`
  );

  // [목적] 프로필 요약 카드 표시값 — 이름/핸들/아바타/활동 수치
  // [이유] 기존 "계정" 첫 행(이름 + @핸들 · 연결됨)을 카드형 요약으로 승격. 이름 텍스트는 이 카드에만 존재해야 한다
  //         (SettingsPage.test — getByText(이름).closest('[role="button"]') 단언과 동일 구조 유지).
  // [사이드 이팩트] 없음 — 읽기 전용 파생값
  // [수정 시 고려사항] followerCount 등은 UserMe 필드 — me 미로드/구버전 응답이면 0 으로 폴백
  const displayName = meLoading ? '' : (me?.displayName ?? authUser?.displayName ?? '—');
  const avatarUrl = me?.avatarUrl ?? null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: ST_BG1, display: 'flex', flexDirection: 'column' }}>
      <SetHeader title="설정" fallbackTo="/profile" />
      <Box
        sx={{
          width: '100%',
          maxWidth: 1040,
          mx: 'auto',
          px: { xs: '16px', lg: '32px' },
          pt: { xs: '16px', lg: '28px' },
          pb: { xs: '28px', lg: '48px' },
          display: 'grid',
          gap: { xs: '14px', lg: '16px 24px' },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '300px minmax(0, 1fr)' },
          gridTemplateRows: { xs: 'auto', lg: 'auto 1fr' },
          gridTemplateAreas: {
            xs: '"profile" "main" "manage"',
            lg: '"profile main" "manage main"',
          },
        }}
      >
        {/* 프로필 요약 카드 — 클릭 시 /settings/profile (기존 계정 첫 행 동작 유지) */}
        <Box
          onClick={() => go('profile')}
          role="button"
          tabIndex={0}
          aria-label="프로필 편집"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              go('profile');
            }
          }}
          sx={{
            gridArea: 'profile',
            alignSelf: 'start',
            bgcolor: ST_SURFACE,
            border: `1px solid ${ST_BORDER}`,
            borderRadius: '16px',
            p: '22px 20px 18px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '4px',
            cursor: 'pointer',
            transition: 'border-color 120ms',
            '&:hover': { borderColor: ST_BRAND },
            '&:focus-visible': { outline: `2px solid ${ST_BRAND}`, outlineOffset: 2 },
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: ST_BRAND,
              color: ST_SURFACE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              fontWeight: 800,
              overflow: 'hidden',
              mb: '8px',
            }}
          >
            {avatarUrl ? (
              <Box
                component="img"
                src={avatarUrl}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              (Array.from(displayName)[0] ?? '')
            )}
          </Box>
          {meLoading ? (
            <Skeleton width={120} height={24} />
          ) : (
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: ST_TEXT_HI }}>
              {displayName}
            </Typography>
          )}
          {meLoading ? (
            <Skeleton width={160} height={16} />
          ) : (
            <Typography sx={{ fontSize: 12, color: ST_TEXT_MID }}>{accountSubLine}</Typography>
          )}
          <Box
            sx={{
              mt: '14px',
              width: '100%',
              display: 'flex',
              borderTop: `1px solid ${ST_BORDER}`,
              pt: '14px',
            }}
          >
            {[
              { n: me?.folioCount ?? 0, l: '폴리오' },
              { n: me?.followerCount ?? 0, l: '팔로워' },
              { n: me?.followingCount ?? 0, l: '팔로잉' },
            ].map((s) => (
              <Box key={s.l} sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: ST_TEXT_HI,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    lineHeight: 1.2,
                  }}
                >
                  {meLoading ? '–' : s.n}
                </Typography>
                <Typography sx={{ fontSize: 11, color: ST_TEXT_MID, mt: '2px' }}>{s.l}</Typography>
              </Box>
            ))}
          </Box>
          <Box
            component="span"
            aria-hidden="true"
            sx={{ mt: '14px', fontSize: 12, fontWeight: 700, color: ST_BRAND }}
          >
            프로필 편집 ›
          </Box>
        </Box>

        {/* 우측 — 설정 섹션 카드 스택 */}
        <Box sx={{ gridArea: 'main', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Section title="프로필">
            <Row
              icon="🌐"
              label="프로필 공개"
              sub={pub ? '다른 사람이 내 폴리오를 볼 수 있어요' : '비공개 — 본인만 열람 가능'}
              right={<Toggle value={pub} onChange={handlePubChange} disabled={meLoading} />}
            />
            <Row
              icon="🏷"
              label="여행 취향 다시 설정"
              sub="추천 정확도 향상"
              right={<Chev />}
              onClick={() => go('taste')}
            />
          </Section>

          {/* D: 알림 prefs — useMe 초기값 + patchMe 디바운스 저장 */}
          <Section title="알림">
            <Row
              icon="❤️"
              label="좋아요·댓글·Fork"
              right={
                <Toggle
                  value={push.social}
                  onChange={(v) => handlePushChange({ ...push, social: v })}
                  disabled={meLoading}
                />
              }
            />
            <Row
              icon="📢"
              label="이벤트·혜택 안내"
              right={
                <Toggle
                  value={push.marketing}
                  onChange={(v) => handlePushChange({ ...push, marketing: v })}
                  disabled={meLoading}
                />
              }
            />
            <Row
              icon="ℹ"
              label="공지 및 업데이트"
              right={
                <Toggle
                  value={push.system}
                  onChange={(v) => handlePushChange({ ...push, system: v })}
                  disabled={meLoading}
                />
              }
            />
          </Section>

          <Section title="데이터">
            <Row
              icon="⬇"
              label="내 폴리오 내보내기"
              sub="준비 중 (v1.1 예정)"
              right={<Chev />}
              onClick={() => go('export')}
            />
            <Row
              icon="🗑"
              label="캐시 삭제"
              sub="로컬 캐시 초기화"
              right={<Chev />}
              onClick={handleClearCache}
            />
          </Section>

          <Section title="정보">
            <Row icon="📄" label="이용약관" right={<Chev />} onClick={() => go('tos')} />
            <Row
              icon="🔐"
              label="개인정보 처리방침"
              right={<Chev />}
              onClick={() => go('privacy')}
            />
            <Row icon="❓" label="고객센터·문의" right={<Chev />} onClick={() => go('help')} />
            <Row icon="ℹ" label="버전" sub="1.0.0 (build 24)" />
          </Section>
        </Box>

        {/* 계정 관리 — lg 에서는 좌측(프로필 카드 아래), 그 미만에서는 맨 아래 */}
        <Box sx={{ gridArea: 'manage', alignSelf: 'start' }}>
          <Section title="계정 관리">
            {SHOW_EMAIL_ROW && (
              <Row
                icon="✉"
                label="이메일"
                sub={meLoading ? <Skeleton width={140} height={14} /> : (me?.emailMasked ?? '—')}
                right={<Chev />}
                onClick={() => go('email')}
              />
            )}
            <Row icon="🔒" label="비밀번호 변경" right={<Chev />} onClick={() => go('password')} />
            <Row icon="↩" label="로그아웃" onClick={handleLogout} right={<Chev />} />
            {/* [수정: 디자인 개편] 이미 /settings/delete 전용 화면으로 바로 이동하는데도 "고객 지원에 문의"라는
              오래된 안내 문구가 남아 있었다 — 실제 동작에 맞게 수정. */}
            <Row
              icon="✕"
              label="회원 탈퇴"
              sub="계정과 데이터를 삭제합니다"
              danger
              right={<Chev />}
              onClick={() => go('delete')}
            />
          </Section>
        </Box>

        <Box
          sx={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            p: '4px 18px 0',
            fontSize: 10,
            color: ST_TEXT_LOW,
            lineHeight: 1.6,
          }}
        >
          강원캔버스 · GangwonCanvas
          <br />© 2025 GangwonCanvas Team
        </Box>
      </Box>
    </Box>
  );
}
